import { useEffect, useMemo, useState } from 'react';
import useEditFormScroll, { editHighlightStyle } from '../hooks/useEditFormScroll';
import BanquetHelpPanel from './BanquetHelpPanel';

function BanquetLayoutMaster({ token, selectedProperty, selectedOutletId = '', showHelp = false, onOpenFaq }) {
  const chainId = String(selectedProperty?.chain_id || '').trim();
  const propertyId = String(selectedProperty?.id || '').trim();
  const outletId = String(selectedOutletId || '').trim();
  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
      'x-chain-id': chainId,
      'x-property-id': propertyId,
      ...(outletId ? { 'x-outlet-id': outletId } : {})
    }),
    [token, chainId, propertyId, outletId]
  );

  const { formRef, editHighlight, scrollToForm } = useEditFormScroll();
  const [halls, setHalls] = useState([]);
  const [seatingTypes, setSeatingTypes] = useState([]);
  const [rows, setRows] = useState([]);
  const [editingId, setEditingId] = useState('');
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    hall_id: '',
    layout_name: '',
    seating_type: 'STANDARD',
    layout_capacity: 50,
    is_active: true
  });

  const parseJson = async (res) => {
    const text = await res.text();
    try {
      const json = text ? JSON.parse(text) : {};
      return { data: json, error: json?.error || '' };
    } catch {
      return { data: null, error: text || '' };
    }
  };

  const loadHalls = async () => {
    const res = await fetch('/api/v1/banquet/halls', { headers });
    const { data, error: apiError } = await parseJson(res);
    if (!res.ok) return setError(apiError || 'Failed to load halls');
    const list = Array.isArray(data) ? data : [];
    setHalls(list);
    if (!form.hall_id && list.length) setForm((prev) => ({ ...prev, hall_id: String(list[0].hall_id) }));
  };

  const loadSeatingTypes = async () => {
    const res = await fetch('/api/v1/banquet/seating-types', { headers });
    const { data, error: apiError } = await parseJson(res);
    if (!res.ok) return setError(apiError || 'Failed to load seating types');
    const list = Array.isArray(data) ? data : [];
    setSeatingTypes(list);
    if (list.length && !list.some((s) => String(s.seating_type_code) === String(form.seating_type))) {
      setForm((prev) => ({ ...prev, seating_type: String(list[0].seating_type_code || 'STANDARD') }));
    }
  };

  const loadLayouts = async () => {
    const q = new URLSearchParams();
    if (form.hall_id) q.set('hall_id', form.hall_id);
    const res = await fetch(`/api/v1/banquet/layouts?${q.toString()}`, { headers });
    const { data, error: apiError } = await parseJson(res);
    if (!res.ok) return setError(apiError || 'Failed to load layouts');
    setRows(Array.isArray(data) ? data : []);
  };

  useEffect(() => {
    if (!chainId || !propertyId) return;
    Promise.all([loadHalls(), loadSeatingTypes()]).then(loadLayouts).catch((err) => setError(err?.message || 'Failed to load masters'));
  }, [chainId, propertyId, outletId]);

  useEffect(() => {
    if (chainId && propertyId) loadLayouts().catch((err) => setError(err?.message || 'Failed to load layouts'));
  }, [form.hall_id]);

  const save = async () => {
    setError('');
    if (!form.hall_id || !form.layout_name || Number(form.layout_capacity || 0) <= 0) {
      return setError('hall, layout name and layout capacity are required');
    }
    const url = editingId ? `/api/v1/banquet/layouts/${editingId}` : `/api/v1/banquet/halls/${form.hall_id}/layouts`;
    const method = editingId ? 'PUT' : 'POST';
    const body = editingId
      ? {
        hall_id: Number(form.hall_id),
        layout_name: form.layout_name,
        seating_type: form.seating_type,
        layout_capacity: Number(form.layout_capacity),
        is_active: form.is_active !== false
      }
      : {
        layout_name: form.layout_name,
        seating_type: form.seating_type,
        layout_capacity: Number(form.layout_capacity),
        is_active: form.is_active !== false
      };

    const res = await fetch(url, {
      method,
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    const { error: apiError } = await parseJson(res);
    if (!res.ok) return setError(apiError || 'Save failed');
    setEditingId('');
    setForm((prev) => ({ ...prev, layout_name: '', seating_type: 'STANDARD', layout_capacity: 50, is_active: true }));
    loadLayouts();
  };

  const edit = (r) => {
    setEditingId(String(r.layout_id));
    setForm({
      hall_id: String(r.hall_id),
      layout_name: r.layout_name || '',
      seating_type: r.seating_type || 'STANDARD',
      layout_capacity: Number(r.layout_capacity || 0),
      is_active: r.is_active !== false
    });
    scrollToForm();
  };

  const deactivate = async (layoutId) => {
    setError('');
    const res = await fetch(`/api/v1/banquet/layouts/${layoutId}`, { method: 'DELETE', headers });
    const { error: apiError } = await parseJson(res);
    if (!res.ok) return setError(apiError || 'Deactivate failed');
    loadLayouts();
  };

  return (
    <div className="page-shell">
      <h2 style={{ color: 'var(--theme-primary)' }}>Banquet - Layout Master</h2>
      <BanquetHelpPanel visible={showHelp} onOpenFaq={onOpenFaq} />
      {error ? <div className="error-banner">{error}</div> : null}

      <div className="filter-bar" ref={formRef} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: '0.6rem', ...editHighlightStyle(editHighlight) }}>
        <select value={form.hall_id} onChange={(e) => setForm({ ...form, hall_id: e.target.value })}>
          <option value="">Select hall</option>
          {halls.map((h) => <option key={h.hall_id} value={h.hall_id}>{h.hall_name}</option>)}
        </select>
        <input placeholder="Layout name" value={form.layout_name} onChange={(e) => setForm({ ...form, layout_name: e.target.value })} />
        <select value={form.seating_type} onChange={(e) => setForm({ ...form, seating_type: e.target.value })}>
          <option value="">Select seating type</option>
          {seatingTypes.map((s) => <option key={s.seating_type_id} value={s.seating_type_code}>{s.seating_type_name}</option>)}
        </select>
        <input placeholder="Layout capacity" type="number" min="1" value={form.layout_capacity} onChange={(e) => setForm({ ...form, layout_capacity: e.target.value })} />
        <label><input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Active</label>
        <button onClick={save}>{editingId ? 'Update Layout' : 'Add Layout'}</button>
        {editingId ? <button onClick={() => { setEditingId(''); setForm((prev) => ({ ...prev, layout_name: '', seating_type: 'STANDARD', layout_capacity: 50, is_active: true })); }}>Cancel</button> : null}
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Hall</th>
              <th style={{ textAlign: 'left' }}>Layout</th>
              <th style={{ textAlign: 'left' }}>Seating</th>
              <th style={{ textAlign: 'right' }}>Capacity</th>
              <th style={{ textAlign: 'left' }}>Active</th>
              <th style={{ textAlign: 'left' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.layout_id}>
                <td>{r.hall_name}</td>
                <td>{r.layout_name}</td>
                <td>{r.seating_type}</td>
                <td style={{ textAlign: 'right' }}>{r.layout_capacity}</td>
                <td>{r.is_active ? 'Yes' : 'No'}</td>
                <td>
                  <button onClick={() => edit(r)}>Edit</button>
                  <button onClick={() => deactivate(r.layout_id)} style={{ marginLeft: '0.4rem' }}>Deactivate</button>
                </td>
              </tr>
            ))}
            {!rows.length ? <tr><td colSpan={6}>No layouts found.</td></tr> : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default BanquetLayoutMaster;
